import { range } from "@dhx/ts-common/core";
import { findIndex } from "@dhx/ts-common/core";

export const locale = {
	monthsShort: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
	months: [
		"January",
		"February",
		"March",
		"April",
		"May",
		"June",
		"July",
		"August",
		"September",
		"October",
		"November",
		"December",
	],
	daysShort: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
	days: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Monday"],
	cancel: "Cancel",
};

/*
	%d	day as a number with leading zero, 01..31
	%j	day as a number, 1..31
	%D	short name of the day, Su Mo Tu...
	%l	full name of the day, Sunday Monday Tuesday...
	%m	month as a number with leading zero, 01..12
	%n	month as a number, 1..12
	%M	short name of the month, Jan Feb Mar...
	%F	full name of the month, January February March...
	%y	year as a number, 2 digits
	%Y	year as a number, 4 digits
	%h	hours 12-format with leading zero, 01..12)
	%g	hours 12-format, 1..12)
	%H	hours 24-format with leading zero, 01..24
	%G	hours 24-format, 1..24
	%i	minutes with leading zero, 01..59
	%s	seconds with leading zero, 01..59
	%a	am or pm
	%A	AM or PM
	%u	milliseconds
*/

const formatters = {
	"%d": (date: Date) => {
		const day = date.getDate();
		return day < 10 ? "0" + day : day;
	},
	"%j": (date: Date) => date.getDate(),
	"%l": (date: Date) => {
		return locale.days[date.getDay()];
	},
	"%D": (date: Date) => {
		return locale.daysShort[date.getDay()];
	},
	"%m": (date: Date) => {
		const month = date.getMonth() + 1;
		return month < 10 ? "0" + month : month;
	},
	"%n": (date: Date) => date.getMonth() + 1,
	"%M": (date: Date) => locale.monthsShort[date.getMonth()],
	"%F": (date: Date) => locale.months[date.getMonth()],
	"%y": (date: Date) =>
		date
			.getFullYear()
			.toString()
			.slice(2),
	"%Y": (date: Date) => date.getFullYear(),
	"%h": (date: Date) => {
		let hours = date.getHours() % 12;
		if (hours === 0) {
			hours = 12;
		}
		return hours < 10 ? "0" + hours : hours;
	},
	"%g": (date: Date) => {
		let hours = date.getHours() % 12;
		if (hours === 0) {
			hours = 12;
		}
		return hours;
	},
	"%H": (date: Date) => {
		const hours = date.getHours();
		return hours < 10 ? "0" + hours : hours;
	},
	"%G": (date: Date) => date.getHours(),
	"%i": (date: Date) => {
		const minutes = date.getMinutes();
		return minutes < 10 ? "0" + minutes : minutes;
	},
	"%s": (date: Date) => {
		const seconds = date.getSeconds();
		return seconds < 10 ? "0" + seconds : seconds;
	},
	"%a": (date: Date) => {
		return date.getHours() >= 12 ? "pm" : "am";
	},
	"%A": (date: Date) => {
		return date.getHours() >= 12 ? "PM" : "AM";
	},
	"%u": (date: Date) => date.getMilliseconds(),
};

const setFormatters = {
	"%d": (date: Date, value: string) => {
		const check = /(^([0-9][0-9])$)/i.test(value);
		check ? date.setDate(Number(value)) : date.setDate(Number(1));
	},
	"%j": (date: Date, value: string) => {
		const check = /(^([0-9]?[0-9])$)/i.test(value);
		check ? date.setDate(Number(value)) : date.setDate(Number(1));
	},
	"%m": (date: Date, value: string) => {
		const check = /(^([0-9][0-9])$)/i.test(value);
		check ? date.setMonth(Number(value) - 1) : date.setMonth(Number(0));
		if (check && date.getMonth() !== Number(value) - 1) date.setMonth(Number(value) - 1);
	},
	"%n": (date: Date, value: string) => {
		const check = /(^([0-9]?[0-9])$)/i.test(value);
		check ? date.setMonth(Number(value) - 1) : date.setMonth(Number(0));
		if (check && date.getMonth() !== Number(value) - 1) date.setMonth(Number(value) - 1);
	},
	"%M": (date: Date, value: string) => {
		const index = findIndex(locale.monthsShort, v => v === value);
		index === -1 ? date.setMonth(0) : date.setMonth(index);
		if (index !== -1 && date.getMonth() !== index) date.setMonth(index);
	},
	"%F": (date: Date, value: string) => {
		const index = findIndex(locale.months, v => v === value);
		index === -1 ? date.setMonth(0) : date.setMonth(index);
		if (index !== -1 && date.getMonth() !== index) date.setMonth(index);
	},
	"%y": (date: Date, value: string) => {
		const check = /(^([0-9][0-9])$)/i.test(value);
		check ? date.setFullYear(Number("20" + value)) : date.setFullYear(Number("2000"));
	},
	"%Y": (date: Date, value: string) => {
		const check = /(^([0-9][0-9][0-9][0-9])$)/i.test(value);
		check ? date.setFullYear(Number(value)) : date.setFullYear(Number("2000"));
	},
	"%h": (date: Date, value: string, dateFormat: string) => {
		const check = /(^0[1-9]|1[0-2]$)/i.test(value);
		(check && dateFormat === "pm") || dateFormat === "PM"
			? date.setHours(Number(value))
			: date.setHours(Number(0));
	},
	"%g": (date: Date, value: string, dateFormat: string) => {
		const check = /(^[1-9]$)|(^0[1-9]|1[0-2]$)/i.test(value);
		(check && dateFormat === "pm") || dateFormat === "PM"
			? date.setHours(Number(value))
			: date.setHours(Number(0));
	},
	"%H": (date: Date, value: string) => {
		const check = /(^[0-2][0-9]$)/i.test(value);
		check ? date.setHours(Number(value)) : date.setHours(Number(0));
	},
	"%G": (date: Date, value: string) => {
		const check = /(^[1-9][0-9]?$)/i.test(value);
		check ? date.setHours(Number(value)) : date.setHours(Number(0));
	},
	"%i": (date: Date, value: string) => {
		const check = /(^([0-5][0-9])$)/i.test(value);
		check ? date.setMinutes(Number(value)) : date.setMinutes(Number(0));
	},
	"%s": (date: Date, value: string) => {
		const check = /(^([0-5][0-9])$)/i.test(value);
		check ? date.setSeconds(Number(value)) : date.setSeconds(Number(0));
	},
	"%u": (date: Date, value: string) => {
		const check = /(^([0-9][0-9][0-9])$)/i.test(value);
		check ? date.setMilliseconds(Number(value)) : date.setMilliseconds(Number(0));
	},
	"%a": (date: Date, value: string) => {
		if (value === "pm") {
			date.setHours(date.getHours() + 12);
		}
	},
	"%A": (date: Date, value: string) => {
		if (value === "PM") {
			date.setHours(date.getHours() + 12);
		}
	},
};

enum TokenType {
	separator,
	datePart,
}

function tokenizeFormat(format: string): IToken[] {
	const tokens = [];
	let currentSeparator = "";
	for (let i = 0; i < format.length; i++) {
		if (format[i] === "%") {
			if (currentSeparator.length > 0) {
				tokens.push({
					type: TokenType.separator,
					value: currentSeparator,
				});
				currentSeparator = "";
			}
			tokens.push({
				type: TokenType.datePart,
				value: format[i] + format[i + 1],
			});
			i++;
		} else {
			currentSeparator += format[i];
		}
	}
	if (currentSeparator.length > 0) {
		tokens.push({
			type: TokenType.separator,
			value: currentSeparator,
		});
	}
	return tokens;
}

export function getFormattedDate(format: string, date: Date): string {
	return tokenizeFormat(format).reduce((res, token) => {
		if (token.type === TokenType.separator) {
			return res + token.value;
		} else {
			if (!formatters[token.value]) {
				return res;
			}
			return res + formatters[token.value](date);
		}
	}, "");
}

interface IToken {
	type: TokenType;
	value: string;
}

export function stringToDate(str: string, format: string, validate?: boolean): any {
	if (typeof str !== "string") {
		return;
	}
	const tokens = tokenizeFormat(format);
	const dateParts = [];
	let index = 0;
	let formatter = null;

	for (const token of tokens) {
		if (token.type === TokenType.separator) {
			const sepratorIndex = str.indexOf(token.value, index);
			if (sepratorIndex === -1) {
				if (validate) {
					return false;
				}
				throw new Error(
					"Incorrect date, see docs: https://docs.dhtmlx.com/suite/calendar__api__calendar_dateformat_config.html"
				);
			}
			if (formatter) {
				dateParts.push({
					formatter,
					value: str.slice(index, sepratorIndex),
				});
				formatter = null;
			}
			index = sepratorIndex + token.value.length;
		} else if (token.type === TokenType.datePart) {
			formatter = token.value;
		}
	}
	if (formatter === "%A" || formatter === "%a") {
		dateParts.unshift({
			formatter,
			value: str.slice(index),
		});
	} else if (formatter) {
		dateParts.push({
			formatter,
			value: str.slice(index),
		});
	}

	dateParts.reverse();

	let dateFormat;
	for (const datePart of dateParts) {
		if (datePart.formatter === "%A" || datePart.formatter === "%a") {
			dateFormat = datePart.value;
		}
	}

	const date = new Date(0);
	for (const datePart of dateParts) {
		if (setFormatters[datePart.formatter]) {
			setFormatters[datePart.formatter](date, datePart.value, dateFormat);
		}
	}

	return validate ? true : date;
}

export class DateHelper {
	static nullTimestampDate = new Date(0);
	static copy(d: Date): Date {
		return new Date(d);
	}
	static fromYear(year: number): Date {
		return new Date(year, 0, 1);
	}
	static fromYearAndMonth(year: number, month: number): Date {
		return new Date(year, month, 1);
	}
	static weekStart(d: Date, firstWeekday: -1 | 0 | 1): Date {
		const diff = (d.getDay() + 7 - firstWeekday) % 7;
		return new Date(d.getFullYear(), d.getMonth(), d.getDate() - diff);
	}
	static monthStart(d: Date): Date {
		return new Date(d.getFullYear(), d.getMonth(), 1);
	}
	static yearStart(d: Date): Date {
		return new Date(d.getFullYear(), 0, 1);
	}
	static dayStart(d: Date): Date {
		return new Date(d.getFullYear(), d.getMonth(), d.getDate());
	}
	static addDay(d: Date, count = 1): Date {
		return new Date(d.getFullYear(), d.getMonth(), d.getDate() + count);
	}
	static addMonth(d: Date, count = 1): Date {
		return new Date(d.getFullYear(), d.getMonth() + count);
	}
	static addYear(d: Date, count = 1): Date {
		return new Date(d.getFullYear() + count, d.getMonth());
	}
	static withHoursAndMinutes(d: Date, hours: number, minutes: number, dateFormat: boolean): Date {
		if (dateFormat === undefined || (!dateFormat && hours === 12) || (dateFormat && hours !== 12)) {
			return new Date(d.getFullYear(), d.getMonth(), d.getDate(), hours, minutes);
		} else if (dateFormat && hours === 12) {
			return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, minutes);
		} else {
			return new Date(d.getFullYear(), d.getMonth(), d.getDate(), hours + 12, minutes);
		}
	}
	static setMonth(d: Date, month: number): void {
		d.setMonth(month);
	}
	static setYear(d: Date, year: number): void {
		d.setFullYear(year);
	}
	static mergeHoursAndMinutes(source: Date, target: Date): Date {
		return new Date(
			source.getFullYear(),
			source.getMonth(),
			source.getDate(),
			target.getHours(),
			target.getMinutes()
		);
	}
	static isWeekEnd(d: Date): boolean {
		return d.getDay() === 0 || d.getDay() === 6;
	}
	static getTwelweYears(d: Date): number[] {
		const y = d.getFullYear();
		const firstYear = y - (y % 12);
		return range(firstYear, firstYear + 11);
	}
	static getDayOrdinal(d: Date) {
		const dayMS = 24 * 60 * 60 * 1000;
		return (d.valueOf() - DateHelper.yearStart(d).valueOf()) / dayMS;
	}
	static getWeekNumber(d: Date): number {
		if (d.getDay() !== 6) {
			d = DateHelper.addDay(d, 6 - d.getDay());
		}
		const dayMS = 24 * 60 * 60 * 1000;
		let ordinal = DateHelper.getDayOrdinal(d);
		if (ordinal - d.getDay() < 0) {
			d = new Date(d.getFullYear() - 1, 12, 0);
			ordinal = DateHelper.getDayOrdinal(d);
		}
		return Math.floor((ordinal - d.getDay() + 10) / 7);
	}
	static isSameDay(d1: Date, d2: Date): boolean {
		return (
			d1.getFullYear() === d2.getFullYear() &&
			d1.getMonth() === d2.getMonth() &&
			d1.getDate() === d2.getDate()
		);
	}
	static toDateObject(date: Date | string, dateFormat: string): Date {
		if (typeof date === "string") {
			return stringToDate(date, dateFormat);
		} else {
			return new Date(date);
		}
	}
}
